import { IBcfImportState, BcfImportStep, IBcfMappingConfig } from './interfaces/IBcfImportState';
import { BcfImportActionKeys } from './BcfImport.actions';

const getInitialMappingConfigs = (): IBcfMappingConfig => ({
  status: { bcfValues: [], mappingRules: {}, isValid: false },
  type: { bcfValues: [], mappingRules: {}, isValid: false },
  priority: { bcfValues: [], mappingRules: {}, isValid: false },
  user: { bcfValues: [], mappingRules: {}, isValid: false },
  stage: { bcfValues: [], mappingRules: {}, isValid: false },
  labels: { bcfValues: [], mappingRules: {}, isValid: false },
});

const initialState: IBcfImportState = {
  selectedFile: undefined,
  fileAnalysisResult: undefined,
  currentStep: BcfImportStep.FILE_UPLOAD,
  isStepValid: false,
  mappingConfigs: getInitialMappingConfigs(),
  importSettings: {
    updateExistingIssues: false,
    createNewActivities: true,
    attachFiles: true,
    skipUnmappedItems: false,
    defaultPointName: 'BCF Import',
  },
  importPreview: undefined,
  importResult: undefined,
  isLoading: false,
  isSaving: false,
  importProgress: 0,
  error: undefined,
  successMessage: undefined,
};

export const bcfImportReducer = (state = initialState, action: any): IBcfImportState => {
  switch (action.type) {
    // File Upload
    case BcfImportActionKeys.UPLOAD_BCF_FILE:
      return {
        ...state,
        selectedFile: action.payload,
        isLoading: false,
        error: undefined,
      };

    case BcfImportActionKeys.ANALYZE_BCF_FILE_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: undefined,
      };

    case BcfImportActionKeys.ANALYZE_BCF_FILE_SUCCESS: {
      // .NET API returns PascalCase
      const payload = action.payload;
      const bcfValues = payload.BcfValues || {};

      return {
        ...state,
        fileAnalysisResult: {
          fileId: payload.FileId,
          bcfValues: {
            types: bcfValues.Types || [],
            statuses: bcfValues.Statuses || [],
            priorities: bcfValues.Priorities || [],
            users: bcfValues.Users || [],
            stages: bcfValues.Stages || [],
            labels: bcfValues.Labels || [],
            totalTopics: bcfValues.TotalTopics || 0,
            topicsWithViewpoints: bcfValues.TopicsWithViewpoints || 0,
            totalViewpoints: bcfValues.TotalViewpoints || 0,
            topics: (bcfValues.Topics || []).map((t: any) => ({
              guid: t.Guid,
              title: t.Title,
              description: t.Description,
              topicStatus: t.TopicStatus,
              topicType: t.TopicType,
              priority: t.Priority,
              creationAuthor: t.CreationAuthor,
              assignedTo: t.AssignedTo,
              dueDate: t.DueDate,
              viewpointCount: t.ViewpointCount,
              labels: t.Labels || [],
            })),
          },
        },
        isLoading: false,
        isStepValid: true,
        mappingConfigs: {
          ...getInitialMappingConfigs(),
          status: { ...getInitialMappingConfigs().status, bcfValues: bcfValues.Statuses || [] },
          type: { ...getInitialMappingConfigs().type, bcfValues: bcfValues.Types || [] },
          priority: { ...getInitialMappingConfigs().priority, bcfValues: bcfValues.Priorities || [] },
          user: { ...getInitialMappingConfigs().user, bcfValues: bcfValues.Users || [] },
          stage: { ...getInitialMappingConfigs().stage, bcfValues: bcfValues.Stages || [] },
          labels: { ...getInitialMappingConfigs().labels, bcfValues: bcfValues.Labels || [] },
        },
      };
    }

    case BcfImportActionKeys.ANALYZE_BCF_FILE_FAILED:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        isStepValid: false,
      };

    // Data Mapping
    case BcfImportActionKeys.UPDATE_FIELD_MAPPING: {
      const { fieldType, mapping } = action.payload;
      return {
        ...state,
        error: undefined,
        mappingConfigs: {
          ...state.mappingConfigs,
          [fieldType]: mapping,
        },
      };
    }

    case BcfImportActionKeys.FETCH_MAPPING_DEFAULTS_REQUEST:
      return {
        ...state,
        isLoading: true,
      };

    case BcfImportActionKeys.FETCH_MAPPING_DEFAULTS_SUCCESS:
      return {
        ...state,
        isLoading: false,
      };

    case BcfImportActionKeys.FETCH_MAPPING_DEFAULTS_FAILED:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case BcfImportActionKeys.VALIDATE_MAPPING_SUCCESS:
      return {
        ...state,
        isStepValid: true,
      };

    // Settings
    case BcfImportActionKeys.UPDATE_IMPORT_SETTINGS:
      return {
        ...state,
        error: undefined,
        importSettings: {
          ...state.importSettings,
          ...action.payload,
        },
      };

    // Summary
    case BcfImportActionKeys.GENERATE_PREVIEW_REQUEST:
      return {
        ...state,
        isLoading: true,
      };

    case BcfImportActionKeys.GENERATE_PREVIEW_SUCCESS:
      return {
        ...state,
        importPreview: action.payload,
        isLoading: false,
      };

    case BcfImportActionKeys.GENERATE_PREVIEW_FAILED:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    // Navigation
    case BcfImportActionKeys.SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
        isStepValid: false,
      };

    case BcfImportActionKeys.GO_BACK:
      return {
        ...state,
        error: undefined,
        currentStep: Math.max(state.currentStep - 1, BcfImportStep.FILE_UPLOAD),
      };

    // Execute Import
    case BcfImportActionKeys.EXECUTE_IMPORT_REQUEST:
      return {
        ...state,
        isSaving: true,
        importProgress: 0,
        error: undefined,
      };

    case BcfImportActionKeys.UPDATE_IMPORT_PROGRESS:
      return {
        ...state,
        importProgress: action.payload,
      };

    case BcfImportActionKeys.EXECUTE_IMPORT_SUCCESS: {
      // .NET API returns PascalCase
      const payload = action.payload;
      return {
        ...state,
        isSaving: false,
        importResult: {
          success: payload.Success,
          queued: payload.Queued || false,
          message: payload.Message || '',
          totalTopics: payload.TotalTopics || 0,
          totalImported: payload.ImportedTopics || 0,
          totalFailed: (payload.TotalTopics || 0) - (payload.ImportedTopics || 0),
          warnings: payload.Warnings || [],
          errors: payload.Errors || [],
          succeededItems: payload.SucceededItems || [],
          failedItems: payload.FailedItems || [],
        },
        importProgress: 100,
        successMessage: payload.Queued
          ? 'Importação iniciada em segundo plano'
          : 'Importação BCF concluída com sucesso',
      };
    }

    case BcfImportActionKeys.EXECUTE_IMPORT_FAILED:
      return {
        ...state,
        isSaving: false,
        error: action.payload,
        importProgress: 0,
      };

    // General
    case BcfImportActionKeys.RESET_IMPORT_STATE:
      return initialState;

    case BcfImportActionKeys.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case BcfImportActionKeys.CLEAR_ERROR:
      return {
        ...state,
        error: undefined,
      };

    default:
      return state;
  }
};

export default bcfImportReducer;
